import Link from "next/link";
import getConfig from "next/config";
import classNames from "classnames";

import Price from "@components/Price";
import Status from "@components/Status";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import Ratings from "@components/Ratings";
import ProductCardLabel from "@components/ProductCardLabel";
import ProductListImage from "@components/ProductList/ProductListImage";
import ProductListTitle from "@components/ProductList/ProductListTitle";
import ProductListConfigureButton from "@components/ProductList/ProductListConfigureButton";
import AddToCompare from "@containers/Catalog/Actions/AddToCompare";
import AddToWishlist from "@containers/Catalog/Actions/AddToWishlist";
import ProductListVariations from "@components/ProductList/ProductListVariations";
import { productHelper } from "@lib/helpers";
import Button from "@components/Button";
import ProductFinancing from "@components/ProductFinancing";
import ProductListColorVariations from "@components/ProductList/ProductListColorVariations";

const {
  publicRuntimeConfig: { settings, credex, oney },
} = getConfig();

const isBricoStore = settings.websiteName === "bricostore";

// Brico: offer label top-left; favorite/compare top-RIGHT and HIGH (George). The label's
// right edge (right-[52px]) stops before the ~32px buttons so it never runs under them.
const cornerLabelPos = isBricoStore
  ? "top-10px left-11px right-[52px] lg:top-15px lg:left-16px items-start"
  : "top-0 right-0 items-end";
const actionsPos = isBricoStore
  ? "top-10px right-11px lg:top-15px lg:right-16px"
  : "top-10px left-11px lg:top-15px lg:left-16px";
// Brico: don't clip the offer label's point/eyelet (extend past the box) + small gap between
// labels; width is bounded by the left/right positions above so long text truncates before
// the buttons (max-w-[85%] stays as a safety cap, e.g. the wide Black-Friday listing badge)
const cornerLabelBox = isBricoStore
  ? "overflow-visible gap-1 max-w-[85%]"
  : "truncate max-w-[75%]";

const ProductListItem = ({
  listType,
  onProductClick,
  product,
  deleteFromWishlist,
  hasColorVariationsInCurrentRow,
}) => {
  const {
    url_key,
    sku,
    thumbnail,
    name,
    regular_price,
    lowest_price,
    msrp_price,
    price,
    discount_type,
    reviews_value,
    reviews_count,
    variations_info,
    configurable_options,
  } = product;

  const {
    isDisabled,
    isSaleable,
    isWestaco,
    hasStorePickup,
    isInSpecialCampaign,
    hasPricePerFactor,
    hasPriceUnit,
    isVirtualService,
    isLocalSeller,
    getLabels,
  } = productHelper(product);

  const onRemoveFromWishlist = (event) => {
    event.preventDefault();
    deleteFromWishlist(product);
  };

  const hasColorVariations =
    configurable_options?.length &&
    configurable_options.find((opt) => opt.attribute_code === "culoare_global");

  const hasVariationsInfo = variations_info?.size > 1;
  const hasFinancingOptions = Boolean(
    isLocalSeller &&
      product.installments_price > 0 &&
      ((credex.isEnabled && !isVirtualService) || oney.isEnabled)
  );

  return (
    <li
      className={classNames(
        "w-1/2 sm:w-1/3 p-1 sm:p-2 min-h-330px lg:min-h-400px bg-white",
        {
          "md:w-1/3": listType === "wishlist",
          "lg:w-1/4": listType !== "wishlist",
          "border-transparent md:border-2":
            settings.websiteName !== "bricostore",
        }
      )}
      data-test="PRODUCT_CARD"
      onClick={() => {
        onProductClick(
          product,
          listType === "search" ? { source: "search_result" } : undefined
        );
      }}
    >
      <div
        className={classNames("Product flex flex-col relative", {
          "hover:shadow-mixRedYellow": settings.websiteName !== "bricostore",
          "shadow-raised sm:hover:shadow-raised-hover border-0 rounded-[8px]":
            settings.websiteName === "bricostore",
        })}
      >
        <Link href={`/${url_key}/cpd/${sku}/`}>
          <a title="">
            <ProductListImage thumbnail={thumbnail} name={name} />
          </a>
        </Link>

        {/* Brico: stock badge sits above the product name */}
        {isBricoStore && !isDisabled && (
          <div className="mt-[6px] mb-[2px]">
            <Status product={product} />
          </div>
        )}

        <Link href={`/${url_key}/cpd/${sku}/`}>
          <a title="">
            <ProductListTitle name={name} />
          </a>
        </Link>

        {!isDisabled && (
          <>
            {getLabels().length >= 1 && isInSpecialCampaign && (
              <div
                className={`absolute ${cornerLabelPos} h-auto w-auto flex flex-col ${cornerLabelBox}`}
              >
                {getLabels()
                  .slice(0, 1)
                  .map(({ title, variant }, index) => (
                    <>
                      <ProductCardLabel
                        key={`${title}-${index}`}
                        variant={variant}
                        className="truncate max-w-full"
                      >
                        {title}
                      </ProductCardLabel>

                      <div className="p-0.5">
                        <img
                          src={`${settings.websiteStatics}/images/oferta-de-black-friday-listing.svg`}
                          alt="oferta de black friday"
                        />
                      </div>
                    </>
                  ))}
              </div>
            )}

            {getLabels().length >= 1 && !isInSpecialCampaign && (
              <div
                className={`absolute ${cornerLabelPos} h-auto w-auto flex flex-col ${cornerLabelBox}`}
              >
                {/* Brico: drop the empty slot getLabels() appends (keep any real labels) */}
                {(isBricoStore
                  ? getLabels().filter((label) => label.title)
                  : getLabels()
                ).map(({ title, variant }, index) => (
                  <ProductCardLabel
                    key={`${title}-${index}`}
                    variant={variant}
                    className="truncate max-w-full"
                  >
                    {title}
                  </ProductCardLabel>
                ))}
              </div>
            )}

            {getLabels().length === 0 && isInSpecialCampaign && (
              <div
                className={`absolute ${cornerLabelPos} h-auto w-auto flex flex-col ${cornerLabelBox} p-0.5`}
              >
                <img
                  src={`${settings.websiteStatics}/images/oferta-de-black-friday-listing.svg`}
                  alt="oferta de black friday"
                />
              </div>
            )}

            {hasColorVariationsInCurrentRow && (
              <div className="mt-[8px] h-[56px] overflow-auto scrollbar-none">
                {hasColorVariations && (
                  <ProductListColorVariations product={product} />
                )}
              </div>
            )}

            {hasVariationsInfo && (
              <div className="h-[20px] my-[6px] flex items-center">
                <ProductListVariations variations={variations_info} />
              </div>
            )}

            {!isBricoStore && <Status product={product} />}

            {reviews_count > 0 && (
              <div className="my-[4px]">
                <Ratings
                  reviewsCount={reviews_count}
                  ratingSummary={reviews_value}
                  displayReviewsNo
                  productUrl={`/${url_key}/cpd/${sku}/`}
                />
              </div>
            )}

            {hasFinancingOptions && (
              <div className="py-[6px] cursor-pointer w-fit">
                <ProductFinancing product={product} label="productCard" />
              </div>
            )}

            <div className="mb-0 mt-auto">
              {isWestaco && price && (
                <div className="leading-20 sm:leading-normal">De la</div>
              )}
              <Price
                price={price}
                regularPrice={regular_price}
                lowestPrice={lowest_price}
                msrp={msrp_price}
                formatted
                discountType={discount_type}
                displayDiscount
                size="smaller"
                displayUnit={
                  hasPricePerFactor && hasPriceUnit
                    ? product.price_unit.split("/")[1]
                    : false
                }
              />
            </div>

            {hasVariationsInfo && isSaleable && (
              <div className="mt-14px sm:mt-13px">
                <Button
                  title="Selecteaza varianta"
                  href={`/${url_key}/cpd/${sku}/`}
                  variant="primary"
                  display="block"
                  isCatalog
                />
              </div>
            )}

            {isWestaco && (
              <Link href={`/${url_key}/cpd/${sku}/`}>
                <a title="">
                  <ProductListConfigureButton />
                </a>
              </Link>
            )}

            {(isSaleable || hasStorePickup) &&
              !hasVariationsInfo &&
              !isWestaco && (
                <AddToCart
                  titleOnlyOnMobile={settings.websiteName === "bricostore"}
                  product={product}
                  isCatalog
                  noMargin
                />
              )}
          </>
        )}

        {listType === "wishlist" && (
          <button
            className="mt-3 text-red-brand hover:text-bleu-hover underline text-sm outline-none"
            onClick={onRemoveFromWishlist}
          >
            Sterge
          </button>
        )}

        <div
          className={`absolute ${actionsPos} flex flex-col justify-center items-center space-y-1.5`}
        >
          <AddToWishlist product={product} />
          <AddToCompare product={product} />
        </div>
      </div>
    </li>
  );
};

export default ProductListItem;
